import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { toast } from "sonner";
import { Bell } from "lucide-react";

export default function NotificationBell() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  const load = () => api.get("/admin/notifications").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const unread = (items || []).filter((n) => !n.read).length;

  const markRead = async (id) => { try { await api.post(`/admin/notifications/${id}/read`); load(); } catch {} };
  const markAll = async () => { try { await api.post("/admin/notifications/read-all"); load(); } catch {} };
  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/notifications/generate");
      toast.success("Cek anomali selesai", { description: r.data.summary });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membuat notifikasi"); }
    finally { setBusy(false); }
  };

  const GROUPS = [
    ["rugi", "✗ Dijual di bawah modal", "text-rose-700"],
    ["tipis", "⚠ Di bawah harga saran", "text-amber-700"],
    ["kosong", "⚠ Modal/harga kosong", "text-stone-600"],
    ["habis", "✗ Stok habis — segera restock", "text-rose-700"],
    ["menipis", "📉 Stok menipis (<5)", "text-amber-700"],
  ];

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notif-bell-btn" onClick={() => setOpen(!open)} className="relative p-2 rounded-full bg-white border border-[#E5DECF] hover:bg-amber-50 transition">
        <Bell className="w-4 h-4 text-stone-700" />
        {unread > 0 && (
          <span data-testid="notif-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-black flex items-center justify-center animate-pulse">{unread}</span>
        )}
      </button>
      {open && (
        <div data-testid="notif-panel" className="fixed left-1/2 -translate-x-1/2 top-16 w-[calc(100vw-1.5rem)] max-w-sm sm:absolute sm:left-auto sm:right-0 sm:translate-x-0 sm:top-11 sm:w-[22rem] z-50 max-h-[70vh] overflow-y-auto bg-white rounded-2xl border border-[#E5DECF] shadow-xl p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="font-display font-bold text-sm">🔔 Notifikasi</span>
            <div className="flex gap-2">
              <button data-testid="notif-generate-btn" onClick={generate} disabled={busy} className="text-[11px] font-bold text-emerald-700 hover:underline disabled:opacity-50">{busy ? "Memeriksa…" : "↻ Cek Sekarang"}</button>
              {unread > 0 && <button data-testid="notif-read-all-btn" onClick={markAll} className="text-[11px] font-bold text-stone-500 hover:underline">Tandai semua dibaca</button>}
            </div>
          </div>
          {!items && <p className="text-xs text-stone-400 p-2">Memuat…</p>}
          {items && !items.length && <p data-testid="notif-empty" className="text-xs text-stone-400 p-2">Belum ada notifikasi.</p>}
          {(items || []).map((n) => (
            <div key={n.id} data-testid={`notif-item-${n.id}`} className={`rounded-xl border p-3 text-xs ${n.read ? "border-stone-100 bg-stone-50/50" : "border-amber-200 bg-amber-50/60"}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-bold">{n.title}</div>
                  <div className="text-stone-500 mt-0.5">{new Date(n.created_at).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })} WIB</div>
                  {n.summary && <div className="mt-1 font-semibold text-stone-700">{n.summary}</div>}
                </div>
                {!n.read && <button data-testid={`notif-read-${n.id}`} onClick={() => markRead(n.id)} className="shrink-0 text-[10px] font-bold text-emerald-700 hover:underline">Dibaca ✓</button>}
              </div>
              {n.counts && (
                <div className="mt-2">
                  <button data-testid={`notif-expand-${n.id}`} onClick={() => setExpanded(expanded === n.id ? null : n.id)} className="text-[11px] font-bold text-sky-700 hover:underline">
                    {expanded === n.id ? "▲ Sembunyikan rincian" : "▼ Lihat rincian produk"}
                  </button>
                  {expanded === n.id && (
                    <div className="mt-1.5 space-y-2">
                      {GROUPS.map(([key, label, cls]) => (n.items?.[key] || []).length > 0 && (
                        <div key={key}>
                          <div className={`font-bold ${cls}`}>{label} ({n.counts[key]})</div>
                          <ul className="mt-0.5 space-y-0.5 text-stone-600">
                            {n.items[key].map((it, i) => (
                              <li key={i}>• {it.name}{it.stock !== undefined ? ` — stok sisa ${it.stock}` : it.modal > 0 ? ` — jual ${rupiah(it.price)} / modal ${rupiah(it.modal)}` : " — belum diinput"}</li>
                            ))}
                          </ul>
                          {n.counts[key] > n.items[key].length && <div className="text-stone-400">…dan {n.counts[key] - n.items[key].length} lainnya (lihat tab Cek Modal)</div>}
                        </div>
                      ))}
                      {!(n.counts.rugi || n.counts.tipis || n.counts.kosong || n.counts.habis || n.counts.menipis) && <p className="text-emerald-700 font-bold">✓ Tidak ada anomali harga & stok.</p>}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
